/**
 * Pure chat session reducer (SI-2-02 / S-D8 / S-D9).
 *
 * Shared by the session host registry and the dock client (`useSession`).
 * No IO, no clock, no randomness — replay must be bit-identical (R-S5 / G4).
 *
 * Action kinds (host-assigned `seq`/`ts`/`actorId` enter as SessionAction fields):
 *   chat.open              — bind conversationUid + mode (projection creates the row)
 *   chat.user              — user utterance
 *   chat.assistant         — assistant utterance (complete or batched)
 *   chat.tool              — tool call lifecycle (allowed|denied|ran|failed)
 *   chat.permission.request / chat.permission.resolve
 *   chat.title / chat.archive
 */

export type ChatMode = "operate" | "develop";

export type ChatMessageRow = {
  /** Client-stable id (also the projected message uid when projected). */
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  /** Host seq that introduced this message (for resume/debug). */
  seq: number;
};

export type ChatToolRow = {
  id: string;
  name: string;
  status: "allowed" | "denied" | "ran" | "failed";
  summary?: string;
  inputJson?: string | null;
  reason?: string | null;
  messageId?: string | null;
  seq: number;
};

export type ChatPermissionRow = {
  id: string;
  tool: string;
  status: "pending" | "allow" | "deny";
  inputJson?: string | null;
  reason?: string | null;
  risk?: string | null;
  seq: number;
};

export type ChatState = {
  conversationUid: string | null;
  mode: ChatMode;
  title: string | null;
  archived: boolean;
  messages: ChatMessageRow[];
  tools: ChatToolRow[];
  permissions: ChatPermissionRow[];
};

export type ChatOpenPayload = {
  conversationUid: string;
  mode?: ChatMode | string;
  title?: string | null;
};

export type ChatUserPayload = {
  id: string;
  content: string;
};

export type ChatAssistantPayload = {
  id: string;
  content: string;
};

export type ChatToolPayload = {
  id: string;
  name: string;
  status: ChatToolRow["status"];
  summary?: string;
  inputJson?: string | null;
  reason?: string | null;
  messageId?: string | null;
};

export type ChatPermissionRequestPayload = {
  id: string;
  tool: string;
  inputJson?: string | null;
  reason?: string | null;
  risk?: string | null;
};

export type ChatPermissionResolvePayload = {
  id: string;
  decision: "allow" | "deny";
};

export type ChatTitlePayload = { title: string };
export type ChatArchivePayload = Record<string, never>;

/** Minimal action shape both host SessionAction and client SessionClientAction satisfy. */
export type ChatActionLike = {
  kind: string;
  payload: unknown;
  seq: number;
  actorId?: string;
  ts?: string;
};

export function initialChatState(): ChatState {
  return {
    conversationUid: null,
    mode: "operate",
    title: null,
    archived: false,
    messages: [],
    tools: [],
    permissions: [],
  };
}

function asRecord(p: unknown): Record<string, unknown> {
  return p && typeof p === "object" && !Array.isArray(p)
    ? (p as Record<string, unknown>)
    : {};
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

/**
 * Pure reduce. Unknown action kinds are no-ops (forward-compatible with
 * host-only control actions that may share the log).
 */
export function reduceChat(state: ChatState, action: ChatActionLike): ChatState {
  const p = asRecord(action.payload);
  switch (action.kind) {
    case "chat.open": {
      const uid = str(p.conversationUid) ?? str(p.uid);
      if (!uid) return state;
      const modeRaw = str(p.mode) ?? "operate";
      const mode: ChatMode = modeRaw === "develop" ? "develop" : "operate";
      return {
        ...state,
        conversationUid: uid,
        mode,
        title: str(p.title) ?? state.title,
      };
    }
    case "chat.user": {
      const id = str(p.id);
      const content = str(p.content) ?? "";
      if (!id) return state;
      if (state.messages.some((m) => m.id === id)) return state;
      return {
        ...state,
        messages: [
          ...state.messages,
          { id, role: "user", content, seq: action.seq },
        ],
      };
    }
    case "chat.assistant": {
      const id = str(p.id);
      const content = str(p.content) ?? "";
      if (!id) return state;
      const existing = state.messages.findIndex((m) => m.id === id);
      if (existing >= 0) {
        const next = state.messages.slice();
        next[existing] = {
          ...next[existing]!,
          content,
          seq: action.seq,
        };
        return { ...state, messages: next };
      }
      return {
        ...state,
        messages: [
          ...state.messages,
          { id, role: "assistant", content, seq: action.seq },
        ],
      };
    }
    case "chat.tool": {
      const id = str(p.id);
      const name = str(p.name);
      if (!id || !name) return state;
      const statusRaw = str(p.status) ?? "allowed";
      const status = (
        ["allowed", "denied", "ran", "failed"] as const
      ).includes(statusRaw as ChatToolRow["status"])
        ? (statusRaw as ChatToolRow["status"])
        : "allowed";
      const row: ChatToolRow = {
        id,
        name,
        status,
        summary: str(p.summary) ?? undefined,
        inputJson: str(p.inputJson) ?? null,
        reason: str(p.reason) ?? null,
        messageId: str(p.messageId) ?? null,
        seq: action.seq,
      };
      const idx = state.tools.findIndex((t) => t.id === id);
      if (idx >= 0) {
        const next = state.tools.slice();
        next[idx] = { ...next[idx]!, ...row, seq: action.seq };
        return { ...state, tools: next };
      }
      return { ...state, tools: [...state.tools, row] };
    }
    case "chat.permission.request": {
      const id = str(p.id);
      const tool = str(p.tool);
      if (!id || !tool) return state;
      if (state.permissions.some((x) => x.id === id)) return state;
      return {
        ...state,
        permissions: [
          ...state.permissions,
          {
            id,
            tool,
            status: "pending",
            inputJson: str(p.inputJson) ?? null,
            reason: str(p.reason) ?? null,
            risk: str(p.risk) ?? null,
            seq: action.seq,
          },
        ],
      };
    }
    case "chat.permission.resolve": {
      const id = str(p.id);
      const decision = str(p.decision);
      if (!id || (decision !== "allow" && decision !== "deny")) return state;
      return {
        ...state,
        permissions: state.permissions.map((x) =>
          x.id === id
            ? { ...x, status: decision as "allow" | "deny", seq: action.seq }
            : x,
        ),
      };
    }
    case "chat.title": {
      const title = str(p.title);
      if (title == null) return state;
      return { ...state, title };
    }
    case "chat.archive":
      return { ...state, archived: true };
    default:
      return state;
  }
}

/** Client-facing reduce adapter for `useSession({ reduce })`. */
export function reduceChatClient(
  state: ChatState,
  action: { seq: number; kind: string; payload: unknown; actorId: string; ts: string },
): ChatState {
  return reduceChat(state, action);
}
